/**
 * Known GRS subanchor values for the `wacAnchorJourneyJsm` use case.
 *
 * The `subAnchor` forwarded to the recommendation query must be one of these
 * values. Anything that is not a known subanchor (case-insensitive) is mapped
 * to {@link DEFAULT_SUB_ANCHOR} so GRS always receives a recognised value.
 *
 * Kept as a standalone list so it can be edited or removed without touching the
 * container component.
 */
export const subAnchors = [
	'IT service management',
	'IT tools',
	'ITSM software',
	'IT management solutions',
	'IT request management',
	'ITIL process',
	'AI for service management',
	'IT incident management',
	'service desk tools',
	'service desk alternatives',
	'help desk tools',
	'IT support help desk',
	'customer support help desk',
	'ticketing tools',
	'customer support ticketing',
	'ticket tracking',
	'IT support ticketing',
	'ticketing help desk',
	'internal ticketing',
	'external ticketing',
	'workflow automation',
	'service management automation',
	'automation tools',
	'service management workflow',
	'enterprise service management',
	'hr service management',
	'finance service management',
	'service portal',
	'facilities service management',
	'marketing service management',
	'ServiceNow',
	'other competitors',
	'Freshservice',
	'BMC Remedy',
	'PagerDuty',
	'Ivanti',
	'ManageEngine',
	'Cherwell',
	'issue tracking',
	'devops tools',
	'incident management',
	'change management',
	'service mapping',
	'incident response',
	'customer service management tools',
	'customer support',
	'client relationship',
	'live chat',
	'marketing core terms',
	'engineering core terms',
	'operations core terms',
	'hr core terms',
	'design core terms',
	'legal core terms',
	'core terms',
	'jsm solution',
	'jsm it management',
	'jsm itsm',
	'jsm ticketing',
	'jsm service desk software',
	'jsm service desk',
	'jsm service desk for business',
	'jsm helpdesk',
	'jsm helpdesk tool',
	'jsm chamados software',
	'jsm chamados helpdesk software',
	'jsm trial',
	'jsm service desk trial',
	'jsm signup',
	'jsm service desk sign up',
	'jsm free',
	'jsm pricing',
	'jsm reviews',
	'jsm demo',
	'jsm alternatives',
	'jsm api',
	'jsm app',
	'jsm service download',
	'jsm service app',
	'jsm os',
	'jsm features',
	'jsm service tutorial',
	'jsm service desk os',
	'what is jsm?',
	'what is jsm service desk?',
	'it asset management',
	'form software',
	'it operations',
	'it design project management',
	'service optimization',
	'ai jsm',
	'misc.',
	'team communication',
	'jsm platform',
	'jsm product',
	'jsm offerings',
	'jsm editions',
	'jsm architecture',
	'jsm infrastructure',
	'jsm dashboard',
	'Default (catch all)',
	'jsm site',
];

/** Fallback subanchor used when the incoming `subAnchor` is unknown or absent. */
export const DEFAULT_SUB_ANCHOR = 'Default (catch all)';

// Lower-cased set for O(1), case-insensitive membership checks.
const subAnchorsLowerCase = new Set(subAnchors.map((subAnchor) => subAnchor.toLowerCase()));

/**
 * Resolves a raw `subAnchor` to a known subanchor value.
 *
 * Returns the original value (preserving its casing) when it matches a known
 * subanchor case-insensitively; otherwise — including for empty/undefined
 * input — returns {@link DEFAULT_SUB_ANCHOR}.
 */
export const resolveSubAnchor = (subAnchor: string | undefined): string => {
	if (!subAnchor) {
		return DEFAULT_SUB_ANCHOR;
	}

	return subAnchorsLowerCase.has(subAnchor.toLowerCase()) ? subAnchor : DEFAULT_SUB_ANCHOR;
};
